/**
 * The sections of the apps launcher (/apps): which kinds of app sit
 * together, in reading order. An app's kind is its archetype in the
 * registry; an app whose kind is in no section lands under "More".
 *
 * An app offered only in a personal workspace (its `tiers`, ruling R18)
 * sits first, under "For you", whatever its archetype: these are the apps
 * for the person themself.
 */
import type { AppDefinition, SurfaceArchetype } from './registry';
import type { WorkspaceType } from '@/lib/workspaceModulesService';

export interface AppSection {
  title: string;
  hint: string;
  archetypes: readonly SurfaceArchetype[];
}

export const APP_SECTIONS: readonly AppSection[] = [
  { title: 'Bring work in', hint: 'Capture, import and sort what could be done.', archetypes: ['intake-funnel'] },
  { title: 'Rank and decide', hint: 'Put a group in a room and come out with an order.', archetypes: ['rank-a-pool', 'loop'] },
  { title: 'Plan', hint: 'What fits, who gets what, and how it lines up.', archetypes: ['plan-pipeline', 'allocation', 'alignment-map'] },
  { title: 'Review and keep current', hint: 'The checks that keep the record true.', archetypes: ['cadenced-review'] },
  { title: 'The record', hint: 'What was decided, and why.', archetypes: ['record-log', 'brief'] },
  { title: 'Day to day', hint: 'For one person, every day.', archetypes: ['continuous-focus'] },
  { title: 'Offsites', hint: 'A room for a day.', archetypes: ['event-room'] },
];

export const FOR_YOU_SECTION: AppSection = {
  title: 'For you',
  hint: 'Your own work, in your personal workspace.',
  archetypes: [],
};

/** Product talk for each kind of workspace (Doc 03 § Product Tiers). */
export const WORKSPACE_TYPE_LABELS: Record<WorkspaceType, string> = {
  personal: 'Individual',
  team: 'Team',
  organizational: 'Organization',
};

const ALL_WORKSPACE_TYPES = Object.keys(WORKSPACE_TYPE_LABELS) as WorkspaceType[];

/**
 * Whether the launcher offers this app in a workspace of this type: only if
 * the app's `tiers` name that type. Until the workspace's type has been read,
 * only the apps offered in every kind are shown, so nothing appears and then
 * vanishes.
 */
export function offeredInWorkspace(app: AppDefinition, workspaceType: WorkspaceType | null): boolean {
  if (workspaceType === null) return ALL_WORKSPACE_TYPES.every((t) => app.tiers.includes(t));
  return app.tiers.includes(workspaceType);
}

/** An app for one person alone: offered in a personal workspace and nowhere else. */
export function isIndividualApp(app: AppDefinition): boolean {
  return app.tiers.length > 0 && app.tiers.every((t) => t === 'personal');
}

/**
 * Whether an app is a card on the launcher and in the catalog: Start is the
 * front door (the hero), not a peer app; Focus is not a phone destination.
 */
export function isLauncherApp(app: AppDefinition, isMobile: boolean): boolean {
  return app.appId !== 'start' && !(isMobile && app.appId === 'focus');
}

export function groupApps(apps: readonly AppDefinition[]): Array<AppSection & { apps: AppDefinition[] }> {
  const placed = new Set<string>();
  const forYou = apps.filter(isIndividualApp);
  forYou.forEach((app) => placed.add(app.appId));
  const groups = APP_SECTIONS.map((section) => {
    const members = apps.filter((app) => !placed.has(app.appId) && section.archetypes.includes(app.archetype));
    members.forEach((app) => placed.add(app.appId));
    return { ...section, apps: members };
  }).filter((group) => group.apps.length > 0);
  if (forYou.length > 0) groups.unshift({ ...FOR_YOU_SECTION, apps: forYou });
  const rest = apps.filter((app) => !placed.has(app.appId));
  if (rest.length > 0) groups.push({ title: 'More', hint: '', archetypes: [], apps: rest });
  return groups;
}
